import Vue from "vue";
import resource from "@core/Util/resources";
import {
    checkpointDefinitionsArray,
    deduplicationBaseCheckpoint,
    filterExclusionCheckpoint
} from "./index";

const ORDER_EXCLUDED = -1337;

/**
 * @type {JourneyBuilder.Checkpoint}
 */
export default class Checkpoint {
    constructor(
        name,
        displayNameResource,
        descriptionResource,
        { iconPath, iconClass }
    ) {
        this.name = name;
        this.iconClass = iconClass;
        this.iconPath = iconPath;

        this._displayNameResource = displayNameResource;
        this._descriptionResource = descriptionResource;
    }

    get defaultOrder(){
        //Default to final position
        return Number.MAX_SAFE_INTEGER;
    }

    get displayName(){
        return resource(this._displayNameResource);
    }

    get description() {
        return resource(this._descriptionResource)
    }

    /**
     * Update the checkpoint `order` property of the given connection config
     * @param {JourneyModelBase} journey
     * @param {JourneyBrickConnection} connection
     */
    static updateOrderForConnection(journey, connection) {
        //By default, take all non ex cluded active checkpoints
        const includedCheckpoints = checkpointDefinitionsArray
            .filter(checkpointDefinition => checkpointDefinition.defaultOrder !== ORDER_EXCLUDED && checkpointDefinition.isActiveForConnection(connection));

        //Dedupbase may still be included in our order
        if(!deduplicationBaseCheckpoint.isActiveForConnection(connection)) {
            const journeyFilters = (journey.config && journey.config.filters) || [];
            const excludedJourneyFilters = (filterExclusionCheckpoint.readForConnection(connection) || {}).excludedFilters || [];

            //journeyFilters are global dedupBase items
            //excludedJourneyFilters can only contain a subset of our journeyFilters, our excludedJourneyFilters will thus always have
            //an equal amount of items or less than our journeyFilters collection
            //when at least a single journeyFilter is not present in our exclusion list, we have an active filter
            //and should thus include our dedupBase checkpoint in our order 
            const hasActiveJourneyFilters = journeyFilters.length !== excludedJourneyFilters.length;

            if(hasActiveJourneyFilters) {
                includedCheckpoints.push(deduplicationBaseCheckpoint);
            }
        }

        const order = includedCheckpoints
            .sort((l, r) => l.defaultOrder > r.defaultOrder ? 1 : 0)
            .map(c => c.name);

        if(order.length) {
            //Ensure reactivity for our components upon (re-)creation
            Vue.set(connection.config, 'order', order);
        } else {
            //Our backend implodes upon encountering an empty order property - offer some help by removing upon getting an empty order
            Vue.delete(connection.config, 'order');
        }
    }

    isConfiguredForConnection(connection){
        //Check if the value is not an empty object
        return !!Object.keys(this.readForConnection(connection)).length;
    }

    /**
     * @param {JourneyBrickConnection} connection
     * @return {Object|undefined}
     */
    readForConnection(connection){
        return connection.config[this.name];
    }

    /**
     * @param {JourneyBrickConnection} connection
     * @return {boolean}
     */
    isActiveForConnection(connection){
        return !!connection.config[this.name];
    }

    /**
     * @param {JourneyModelBase} journey
     * @param {JourneyBrickConnection} connection
     */
    activateForConnection(journey, connection){
        if(!connection.supportedCheckpoints.includes(this.name)) {
            return;
        }

        //Ensure isActive returns true for self
        Vue.set(connection.config, this.name, {});
        Checkpoint.updateOrderForConnection(journey, connection);
    }

    /**
     * @param {JourneyModelBase} journey
     * @param {JourneyBrickConnection} connection
     */
    deactivateForConnection(journey, connection){
        //Vue.delete(connection.config[Checkpoint.CONFIG_PROPERTY], this.name);
        Vue.delete(connection.config, this.name);
        Checkpoint.updateOrderForConnection(journey, connection);
    }

    /**
     * @param {JourneyBrickConnection} connection
     * @returns {JourneyBuilder.Tooltip}
     */
    buildTooltip(connection){ //<-- rename, buildContentTooltip?
        return {
            title: undefined,
            content: undefined
        }
    }

    /**
     * @return {JourneyBuilder.Tooltip}
     */
    buildInformationTooltip() {
        return {
            title: this.displayName,
            content: this.description
        }
    }
}

export {
    ORDER_EXCLUDED
}